import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Admin "login as restaurant" opens this dashboard in a NEW tab and
    // passes a fresh impersonation token via URL query param. Handle that
    // FIRST, before falling back to localStorage — reading a query param
    // on this tab's own initial load is race-free, whereas relying on
    // localStorage written a moment earlier by a DIFFERENT tab/window is not.
    const params = new URLSearchParams(window.location.search);
    const impersonateToken = params.get('impersonate_token');
    if (impersonateToken) {
      localStorage.setItem('restaurant_token', impersonateToken);
      localStorage.removeItem('restaurant_data'); // force a fresh fetch below, don't trust any stale cache
      // Strip the token from the visible URL so it isn't left bookmarkable/shareable
      params.delete('impersonate_token');
      const cleanSearch = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (cleanSearch ? `?${cleanSearch}` : '') + window.location.hash);

      authAPI.getProfile()
        .then(res => {
          setRestaurant(res.data);
          localStorage.setItem('restaurant_data', JSON.stringify(res.data));
        })
        .catch(() => {
          logout();
        })
        .finally(() => setLoading(false));
      return;
    }

    const token = localStorage.getItem('restaurant_token');
    const savedData = localStorage.getItem('restaurant_data');

    if (token && savedData) {
      setRestaurant(JSON.parse(savedData));
      // Verify token is still valid
      authAPI.getProfile()
        .then(res => {
          setRestaurant(res.data);
          localStorage.setItem('restaurant_data', JSON.stringify(res.data));
        })
        .catch(() => {
          logout();
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const response = await authAPI.login({ email, password });
    const { token, restaurant: restaurantData } = response.data;
    
    localStorage.setItem('restaurant_token', token);
    localStorage.setItem('restaurant_data', JSON.stringify(restaurantData));
    setRestaurant(restaurantData);
    
    return restaurantData;
  };

  const register = async (data) => {
    const response = await authAPI.register(data);
    const { token, restaurant: restaurantData } = response.data;
    
    localStorage.setItem('restaurant_token', token);
    localStorage.setItem('restaurant_data', JSON.stringify(restaurantData));
    setRestaurant(restaurantData);
    
    // Hele antwoord teruggeven: de registratie stuurt ook de melding over de
    // verificatiemail mee, en die wil de pagina kunnen tonen.
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('restaurant_token');
    localStorage.removeItem('restaurant_data');
    setRestaurant(null);
  };

  const updateRestaurant = (data) => {
    setRestaurant(prev => ({ ...prev, ...data }));
    localStorage.setItem('restaurant_data', JSON.stringify({ ...restaurant, ...data }));
  };

  return (
    <AuthContext.Provider value={{
      restaurant,
      loading,
      isAuthenticated: !!restaurant,
      login,
      register,
      logout,
      updateRestaurant,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
